import MailboxController       from './MailboxController';
import ViewportHTTPService     from './HTTP/ViewportHTTPService';
import MessagesViewportRequest from "./Entities/MessagesViewportRequest";
import MailboxesIds            from "./Helpers/MailboxesIds";
import MessagesFullSearch      from "./Entities/Messages/MessagesFullSearch";
import FolderItem              from "./Entities/FolderItem";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import StateEventManager       from "@/js/Shared/Events/Managers/StateEventManager";
import BroadcastEventManager   from '@/js/Shared/Events/Managers/BroadcastEventManager';
import SAPOMailConfig          from "@/SAPOMailConfig";
import FoldersController from './FoldersController';
import BrowserNotificationsControllerSingleton from '../Webmail/Notifications/BrowserNotificationsController';
import EventBroadcaster from '../Common/Events/EventBroadcaster';
import Events from '../Common/Events/Events';
import {reactive} from 'vue';

export {ViewportController as default};

/**
* Viewport Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ViewportControllerSingleton
{
  /**
   * Mailbox Controller
   *
   * @public
   *
   * @type {MailboxController}
   *
   * @since 7.0.0
   */
  mailboxes = MailboxController;

  /**
   * Viewport event state
   *
   * @public
   *
   * @type {StateEventManager}
   *
   * @since 7.0.0
   */
  state = reactive(new StateEventManager());

  /**
   * Viewport event message broadcaster
   *
   * @public
   *
   * @type {BroadcastEventManager}
   *
   * @since 7.0.0
   */
  broadcaster = new BroadcastEventManager();

  /**
   * Fetches viewport messages from the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id  folder id
   * @param {Number} slice_size if not given uses the default slice size
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessages(folder_id, slice_size, zone = 'messages_list') {
    if (this.mailboxes.viewport.exists(folder_id)) {
      this.state.setSuccess(`${folder_id}:fetch_foreground`, true);
      return this.fetchMessagesBackground(folder_id, 'fetch_list_background');
    }

    return this.fetchMessagesForeground(folder_id, null, slice_size, zone);
  }

  /**
   * Fetches viewport messages index from the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesIndex(folder_id) {
    if (this.mailboxes.viewport.exists(folder_id)) {
      return true;
    }

    let request   = this.getViewportRequest(folder_id, false);
    request.slice = '0:0';

    let response = await ViewportHTTPService.fetchViewport(request, 'fetch_indexs_to_empty');
    this.mailboxes.handleResponse(response);
    EventBroadcaster.broadcast(Events.VIEWPORT_UPDATED, {folder_id: folder_id}, response.success);
    return response.success;
  }

  /**
   * Fetches Next Messages Slice
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesNext(folder_id) {
    let request        = this.getViewportRequest(folder_id, true, true);
    request.checkcache = 0;

    this.state.setLoading(`${folder_id}:fetch_next`);
    let response = await ViewportHTTPService.fetchViewport(request, 'fetch_next');
    this.mailboxes.handleResponse(response);
    this.state.setSuccess(`${folder_id}:fetch_next`, response.success);
    EventBroadcaster.broadcast(Events.VIEWPORT_UPDATED, {folder_id: folder_id}, response.success);
    return response.success;
  }

  /**
   * Calls the server to quick search inside a mailbox
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} quick_search quick search query object
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesQuickSearch(quick_search) {
    let folder_id = SAPOMailConfig.client.folders.alias.search;
    let request   = this.getViewportRequest(folder_id, false);
    request       = Object.assign(request, quick_search);

    return await this.fetchMessagesForeground(folder_id, request, null,'quick_search');
  }

  /**
   * Calls the server to retrieve/update message list from a given folder with a given order
   *
   * Populates the MessageModel with fetched messages
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id       folder id
   * @param {Number} sort_by         defines sort by
   * @param {string} sort_direction  defines sort direction
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesSort(folder_id, sort_by, sort_direction) {
    let request        = this.getViewportRequest(folder_id, true);
    request.sortdir    = sort_direction;
    request.sortby     = sort_by;
    request.checkcache = 0;

    return this.fetchMessagesForeground(folder_id, request, null, 'sort_messages');
  }

  /**
   * Fetches viewport messages in the background
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesBackground(folder_id, zone) {
    this.state.setLoading(`${folder_id}:fetch_background`);
    let response = await ViewportHTTPService.fetchViewport(
      this.getViewportRequest(folder_id),
      zone
    );

    this.mailboxes.handleResponse(response);
    this.state.setSuccess(`${folder_id}:fetch_background`, response.success);
    EventBroadcaster.broadcast(Events.VIEWPORT_UPDATED, {folder_id: folder_id}, response.success);
    return response.success;
  }

  /**
   * Fetches viewport messages in the foreground
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}                  folder_id  folder id
   * @param {MessagesViewportRequest} request    viewport request
   * @param {Number}                  slice_size if not given uses the default slice size
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessagesForeground(folder_id, request, slice_size, zone) {
    if (!request) {
      request = this.getViewportRequest(folder_id, true, false, slice_size);
    }

    this.state.setLoading(`${folder_id}:fetch_foreground`)
    let response = await ViewportHTTPService.fetchViewport(request, zone);
    this.mailboxes.handleResponse(response);
    let success  = response.success;

    if (response.success === true) {
      success = !this.mailboxes.viewport.get(folder_id).error;
    }
    this.state.setSuccess(`${folder_id}:fetch_foreground`, success);
    EventBroadcaster.broadcast(Events.VIEWPORT_UPDATED, {folder_id: folder_id}, response.success);
    return response.success;
  }

  /**
   * Fetches Messages Uids range list
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Number} range     list range
   *
   * @return {Object} an object with search folder id, and search criteria
   */
  async fetchMessagesRangeList(folder_id, range) {
    let request        = this.getViewportRequest(folder_id);
    request.rangeslice = ":1";
    request.slice      = "1:" + range;

    this.state.setLoading(`${folder_id}:fetch_range`);
    let response = await ViewportHTTPService.fetchViewport(request, 'fetch_range');
    this.mailboxes.handleResponse(response);
    this.state.setSuccess(`${folder_id}:fetch_range`, response.success);
    return response.success;
  }

  /**
   * Fetches Message Full search Criteria with view id
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessagesFullSearch} full_search search_criteria
   * @param {Array}              mboxes      mboxes to search for
   *
   * @return {Object} an object with search folder id, and search criteria
   */
  async fetchMessagesFullSearchCriteria(full_search, mboxes) {
    let results = {
      folder_id : null,
      criteria  : {},
    }

    let response = await ViewportHTTPService.fetchSearchCriteriaFolder(full_search, mboxes);

    if (!response.success) {
      return results;
    }

    for(let k in full_search) {
      if (full_search[k] !== undefined) {
        results.criteria[k] = full_search[k];
      }
    }

    results.criteria.mboxes = mboxes.join(':');
    results.folder_id       = response.results.search_mbox || null;
    return results;
  }

  /**
   * Sets the message by retrieving the message from the mailbox model
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async fetchMessage(folder_id, message_id) {
    // This prevents horde from loading the viewport list when opening the webmail directly on a message, or when refreshing the site
    // if (this.state.events[`${folder_id}:fetch_foreground`] === 'loading' ) {
    //   this.state.on(`${folder_id}:fetch_foreground`, 'success', async () => {
    //     await this.fetchMessage(folder_id, message_id);
    //     this.state.destroy(`${folder_id}:fetch_foreground`, 'success', this);
    //   });

    //   return;
    // }

    let request  = this.getViewportRequest(folder_id, true);
    let viewport = this.mailboxes.viewport.get(folder_id);

    let message  = viewport.get(message_id);

    if (message.flags.read === false && message.full_fetch === true) {
      this.flagRead(folder_id, [message_id]);
    }

    if (message.full_fetch === true) {
      this.state.setSuccess(`${folder_id}:${message_id}`, true);
      return true;
    }

    this.state.setLoading(`${folder_id}:${message_id}`);
    let response = await ViewportHTTPService.fetchMessage(request, message_id);
    this.mailboxes.handleResponse(response);
    this.state.setSuccess(`${folder_id}:${message_id}`, response.success);
    return response.success;
  }

  /**
   * Fetches ta message source
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<String|null>}
   */
  async fetchMessageSource(folder_id, message_id) {
    let response = await ViewportHTTPService.fetchMessageSource(folder_id, message_id);

    if (response.success === false) {
      return null;
    }

    return response.results;
  }

  /**
   * Retrieves the message print url
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<String|null>}
   */
  getMessagePrintURL(message, folder_id) {
    return ViewportHTTPService.getMessagePrintURL(message, folder_id);
  }

  /**
   * Calls the server to retrieve message from a given folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id  folder id
   * @param {String} message_id message id
   *
   * @return {Promise<Boolean>}
   */
  async sendMDNReceipt(folder_id, message_id) {
    let response = await ViewportHTTPService.sendMDNReceipt(folder_id, message_id);
    this.mailboxes.handleResponse(response);
    return response.success;
  }


  /**
   * Moves given messages to trash, or permanently deletes messages if messages are in trash or spam
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id source folder id
   * @param {Array}  messages  messages ids to be moved
   *
   * @return {Promise<Boolean>}
   */
  async trashMessages(folder_id, messages) {
    let request    = this.getViewportRequest(folder_id);
    let collection = this.mailboxes.viewport.get(folder_id);
    request.force = 1;

    collection.event_progress.addEvent('moving', messages);
    let response = await ViewportHTTPService.deleteMessages(request, messages);
    collection.event_progress.deleteEvent('moving');
    this.mailboxes.handleResponse(response);

    if (response.success) {
      let tag = (SAPOMailConfig.client.folders.delete_warning.enabled[folder_id])
              ? 'messages_delete_success'
              : 'messages_trash_success';

      NotificationsController.addSuccess(
        this.getMessageNotificationTag('notifications.' + tag, messages.length),
        [messages.length]
      );

      FoldersController.poll(SAPOMailConfig.client.folders.alias.trash);
    }

    return response.success
  }

  /**
   * Moves given messages messages to another folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}     folder_id          source folder id
   * @param {FolderItem} destination_folder destination folder
   * @param {Array}      messages           messages ids to be moved
   *
   * @return {Promise<Boolean>}
   */
  async moveMessages(folder_id, destination_folder, messages) {
    if (destination_folder.id === '_TOPFOLDER_') {
      NotificationsController.addWarning('notifications.messages_move_top_warning',);
      return true;
    }

    if (folder_id === destination_folder.id) {
      NotificationsController.addWarning('notifications.messages_move_same_warning', [destination_folder.name]);
      return true;
    }

    let request    = this.getViewportRequest(folder_id);
    let collection = this.mailboxes.viewport.get(folder_id);
    request.force = 1;

    collection.event_progress.addEvent('moving', messages);
    let response = await ViewportHTTPService.moveMessages(request, destination_folder.id, messages);
    collection.event_progress.deleteEvent('moving');
    this.mailboxes.handleResponse(response);

    if (response.success) {
      NotificationsController.addSuccess(
        this.getMessageNotificationTag('notifications.messages_move_success', messages.length),
        [messages.length, destination_folder.name]
      );
    }

    return response.success
  }

  /**
   * Marks all given messages as read
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async flagRead(folder_id, messages) {
    let request  = this.getViewportRequest(folder_id, true);
    let response = await ViewportHTTPService.flagMessages(request, messages, ['\\seen'], true);
    this.mailboxes.handleResponse(response);
    return response.success;
  }

  /**
   * Marks all given messages as read
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async flagUnread(folder_id, messages) {
    let request  = this.getViewportRequest(folder_id, true);
    let response = await ViewportHTTPService.flagMessages(request, messages, ['\\seen'], false);
    this.mailboxes.handleResponse(response);
    return response.success;
  }

  /**
   * Marks all given messages as flagged
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async flagFlagged(folder_id, messages) {
    let request  = this.getViewportRequest(folder_id, true);
    let response = await ViewportHTTPService.flagMessages(request, messages, ['\\flagged'], true);
    this.mailboxes.handleResponse(response);
    return response.success;
  }

  /**
   * Marks all given messages as unflagged
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async flagUnflagged(folder_id, messages) {
    let request  = this.getViewportRequest(folder_id, true);
    let response = await ViewportHTTPService.flagMessages(request, messages, ['\\flagged'], false);
    this.mailboxes.handleResponse(response);
    return response.success;
  }

  /**
   * Marks all given messages as spam
   *
   * @spam
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async markSpam(folder_id, messages) {
    if (messages.length > SAPOMailConfig.client.viewport.report_spam_limit) {
      NotificationsController.addError('notifications.messages_spam_report_limit_error', [SAPOMailConfig.client.viewport.report_spam_limit]);
      return false;
    }

    let request    = this.getViewportRequest(folder_id);
    let collection = this.mailboxes.viewport.get(folder_id);
    request.force  = 1;
    collection.event_progress.addEvent('moving', messages);
    let response  = await ViewportHTTPService.markSpam(request, messages, true);
    this.mailboxes.handleResponse(response);
    collection.event_progress.deleteEvent('moving');

    if (response.success === false) {
      return;
    }

    NotificationsController.addSuccess(
      this.getMessageNotificationTag('notifications.messages_spam_success', messages.length),
      [messages.length]
    );
    return true;
  }

  /**
   * Marks all given messages as not spam
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_id folder id
   * @param {Array}  messages  list of messages ids
   *
   * @return {Promise<Boolean>}
   */
  async markNotSpam(folder_id, messages) {
    let request    = this.getViewportRequest(folder_id);
    request.force  = 1;
    let collection = this.mailboxes.viewport.get(folder_id);
    let response   = await ViewportHTTPService.markSpam(request, messages, false);
    collection.event_progress.deleteEvent('moving');
    this.mailboxes.handleResponse(response);

    if (response.success === false) {
      return;
    }

    NotificationsController.addSuccess(
      this.getMessageNotificationTag('notifications.messages_not_spam_success', messages.length),
      [messages.length]
    );

    return true;
  }

  /**
   * Adds _multi prefix to notification tag when count is bigger than 1
   *
   * @spam
   *
   * @since 7.0.0
   *
   * @param {String} tag   notification tag
   * @param {Number} count number of messages
   *
   * @return {String
   */
  getMessageNotificationTag(tag, count) {
    return (count > 1) ? tag + '_multi' : tag;
  }

  sendBrowserNotification(folder_id) {
    let message = MailboxController.viewport.get(folder_id).getLastAdded();

    if (!message) {
      return;
    }

    BrowserNotificationsControllerSingleton.notify(
      message.subject,
      message.sender.name || message.email,
      {folder_id: folder_id, message_id: message.id},
    )
  }

  /**
   * Returns viewport request object
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}           folder_id  folder id
   * @param {Boolean}          cache      use cache
   * @param {Boolean}          next_slice set the slice to the next one
   * @param {Number|undefined} slice_size defines de slice size, if not given uses default
   *
   * @return {Object}
   */
  getViewportRequest(folder_id, cache = true, next_slice = false, slice_size) {
    let request      = new MessagesViewportRequest();
    let viewport     = this.mailboxes.viewport.get(folder_id);
    let default_size = SAPOMailConfig.client.viewport.slice_size;

    if (viewport.slice === 0) {
      viewport.slice = slice_size || default_size;
    }

    if (next_slice === true) {
      viewport.slice += default_size;
      request.checkcache = 0;
    }

    if (cache === false || viewport.count === 0) {
      request.force      = 1;
      request.initial    = 1;
      request.checkcache = 1;
    }

    if (cache === true && viewport.count !== 0) {
      request.cacheid    = viewport.cache_id;
      request.cache      = MailboxesIds.getIdsAsString(Object.keys(viewport.index));
      request.checkcache = 1;
    }

    request.view  = folder_id;
    request.slice = "1:" + viewport.slice;
    return request;
  }
}

const ViewportController = new ViewportControllerSingleton();